import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faExclamationTriangle, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { Badge, Form, Table, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import ExpandableText from "../expandable-text";
import EnabledBadge from "../enabled-badge";
import { Link } from "preact-router";
import CoverStatusBadge from "../cover-status-badge";
import { formatEuro } from "../../lib/utils";



export default function CoverTable({ covers: initialCovers, disabled, onCoversChange }) {
    const [covers, setCovers] = useState(initialCovers ?? []);

    const sortCovers = (covers) => {
        const order = ["included", "automatic", "optional_on", "optional_off", "not_included"];

        let sortedCovers = covers.sort((a, b) => {
            let stateA = a.formula_cover.state;
            let stateB = b.formula_cover.state;
            let indexA = order.indexOf(stateA);
            let indexB = order.indexOf(stateB);

            if (indexA === -1) {
                indexA = 999;
            }
            if (indexB === -1) {
                indexB = 999;
            }

            if (indexA !== indexB) {
                // If states are different, sort by state
                return indexA - indexB;
            } else {
                // If states are the same, sort by position
                return parseInt(a.cover.position) - parseInt(b.cover.position);
            }
        });

        return sortedCovers;
    };

    useEffect(() => {
        setCovers(initialCovers ?? []);
    }, [initialCovers]);

    const handleClick = (e, row) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleChange = (e, row) => {
        let { value } = e.target;

        if (e.target.type === "checkbox" || e.target.type === "radio" || e.target.type === "switch") {
            value = e.target.checked;

            // Explain: if the cover has levels and the value is true, we set the value to the current level
            if (value === true && Array.isArray(row.formula_cover.levels) && row.formula_cover.levels.length > 0) {
                value = getCurrentLevel(row);
            }
        }

        let newCovers = covers.map((cover) => {
            if (cover.cover.key === row.cover.key) {
                return {
                    ...cover,
                    cover_value: {
                        ...cover.cover_value,
                        status: value,
                    },
                };
            }
            return cover;
        });
        setCovers(newCovers);
    };

    const getCoverStatusAsBoolean = (row) => {
        let status = row?.cover_value?.status ?? null;
        let hasLevels = row?.formula_cover?.levels && Array.isArray(row.formula_cover.levels) && row.formula_cover.levels.length > 0;

        if (hasLevels) {
            return status !== null && status !== undefined && status !== "" && status !== "null" && status !== "undefined" && status !== "0" && status !== 0 && status !== false && status !== "false";
        }

        if (parseInt(status) === 1) {
            return true;
        } else if (parseInt(status) === 0) {
            return false;
        }

        if (status === "true") {
            return true;
        } else if (status === "false") {
            return false;
        }

        if (status === "active") {
            return true;
        } else if (status === "inactive") {
            return false;
        }

        if (status === undefined || status === null) {
            return row.formula_cover.state === "optional_on" ? true : false;
        }

        try {
            return JSON.parse(status ?? (row.formula_cover.state === "optional_on" ? true : false));
        } catch (e) {
            console.error(`Error parsing status ${status} to boolean`, row.cover.key, status);
        }

        return false;
    };

    const getCurrentLevel = (row) => {
        let defaultLevel = row?.formula_cover?.default_level ?? null;
        let currentLevel = row?.cover_value?.status ?? null;

        if (row?.formula_cover?.levels && Array.isArray(row.formula_cover.levels) && row.formula_cover.levels.length > 0) {
            let level = row.formula_cover.levels.find((l) => l.key === currentLevel);

            if (level) {
                return level.key;
            }

            if (defaultLevel) {
                let defaultLevelObj = row.formula_cover.levels.find((l) => l.key === defaultLevel);

                if (defaultLevelObj) {
                    return defaultLevelObj.key;
                }
            }

            return row.formula_cover.levels[0].key;
        }

        return currentLevel;
    };

    function getNestedProperty(obj, path) {
        const keys = path.split('.');
        let current = obj;

        if (current === null || current === undefined) {
            return undefined;
        }

        for (const key of keys) {
            if (current === null || current === undefined) {
                return undefined;
            }

            current = current[key];

            if (current === undefined) {
                return undefined; // Handle cases where a property is missing
            }
        }

        return current;
    }

    useEffect(() => {
        if (onCoversChange) {
            onCoversChange(covers);
        }
    }, [covers]);

    const columns = [

        // {
        //     dataField: "cover.key",
        //     text: "Clé",
        //     sort: true,
        //     formatter: (cell, row) => {
        //         return (<Link href={`/save-formula-cover/${row.formula_cover.product_code}/${row.formula_cover.formula_key}/${row.cover.key}`}>
        //             {cell}
        //         </Link>);
        //     }
        // },
        {
            dataField: "cover.name",
            text: "Garantie ",
            sort: true,
            formatter: (cell, row) => {
                return (<>
                    <span className={`cursor-pointer`} onClick={(e) => handleClick(e, row)}>
                        {cell}
                    </span>
                    {row.cover.description ? <OverlayTrigger placement="top" key={'ot-' + row?.id} overlay={<Tooltip id={'tooltip-cover-' + row?.id}>
                        {row?.cover?.description}
                    </Tooltip>}>
                        <FontAwesomeIcon icon={faCircleInfo} className="ms-2 text-muted" />
                    </OverlayTrigger> : null}
                </>
                );
            },
        },
        {
            dataField: "cover_value.deductible",
            text: "Franchise",
            sort: true,
            formatter: (cell, row) => {
                if (!cell) {
                    return "N/A";
                }
                return <ExpandableText style={{ fontSize: "0.8rem" }}>{cell}</ExpandableText>;
            },
        },
        {
            dataField: "cover_value.limit",
            text: "Plafond",
            sort: true,
            formatter: (cell, row) => {
                if (!cell) {
                    return "N/A";
                }
                return <ExpandableText style={{ fontSize: "0.8rem" }}>{cell}</ExpandableText>;
            },
        },
        {
            dataField: "formula_cover.state",
            text: "Type",
            sort: true,
            formatter: (cell, row) => {
                return <CoverStatusBadge coverValue={row?.cover_value} cover={row?.formula_cover} />;
            },
        },
        {
            dataField: "cover_value.premium",
            text: "Prime",
            sort: true,
            formatter: (cell, row) => {
                if (!cell || parseFloat(cell) <= 0) {
                    return "N/A";
                }

                return <OverlayTrigger placement="top" key={'ot-' + row?.id} overlay={<Tooltip id={'tooltip-cover-' + row?.id}>
                    <table>
                        <tbody>
                            <tr>
                                <td>Prime TTC</td>
                                <td>{formatEuro(row?.cover_value?.metadata?.includingTaxes)}</td>
                            </tr>
                            <tr>
                                <td>Prime HT</td>
                                <td>{formatEuro(row?.cover_value?.metadata?.excludingTaxes)}</td>
                            </tr>
                            <tr>
                                <td>Base</td>
                                <td>{formatEuro(row?.cover_value?.metadata?.base)}</td>
                            </tr>
                            <tr>
                                <td>Commission</td>
                                <td>{formatEuro(row?.cover_value?.metadata?.com)}</td>
                            </tr>
                            <tr>
                                <td>Taxe</td>
                                <td>{formatEuro(row?.cover_value?.metadata?.taxes)}</td>
                            </tr>
                        </tbody>
                    </table>
                </Tooltip>}>
                    <a href="#">{formatEuro(cell)}</a>
                </OverlayTrigger>
            },
        },
        {
            dataField: "actions",
            text: "Actions",
            isDummyField: true,
            formatter: (cell, row) => {
                const isCoverActive = getCoverStatusAsBoolean(row);
                return (
                    <div className="d-flex align-items-center justify-content-start gap-2" key={`actions-${row.cover?.key}`}>
                        {["included", "optional_on", "optional_off", "automatic"].includes(row.formula_cover.state) ? (
                            <>
                                {["optional_off", "optional_on"].includes(row.formula_cover.state) ? (
                                    <>
                                        <Form.Switch
                                            key={`switch-${row.cover?.key}`}
                                            size="100"
                                            checked={isCoverActive}
                                            onChange={(e) => handleChange(e, row)}
                                            id={`switch-${row.cover?.key}`}
                                            label=""
                                        />
                                    </>
                                ) : null}

                                {isCoverActive && row.formula_cover?.levels && Array.isArray(row.formula_cover?.levels) && row.formula_cover.levels.length > 0 ? (
                                    <>
                                        {getCurrentLevel(row) !== row.cover_value.status ? (
                                            <>
                                                <Badge bg="warning" className="text-dark">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} /> !=
                                                </Badge>
                                            </>
                                        ) : null}

                                        <Form.Select key={`select-${row.cover?.key}`} size="sm" value={getCurrentLevel(row)} onChange={(e) => handleChange(e, row)} id={`select-${row.cover?.key}`}>
                                            <option hidden value={null}>
                                                Choisir
                                            </option>
                                            {(row?.formula_cover?.levels ?? []).map((level, index) => {
                                                return (
                                                    <option value={level?.key} key={`option-${row.cover.key}-${index}`}>
                                                        {level?.value}
                                                    </option>
                                                );
                                            })}
                                        </Form.Select>
                                    </>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                );
            },
        },
    ];

    if (columns?.length <= 0) {
        return null;
    }

    if (disabled) {
        return (
            <div className="d-flex align-items-center justify-content-center text-muted flex-column gap-2 p-4">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p>Les garanties ne peuvent pas être modifiées pour le moment.</p>
            </div>
        );
    }

    return (
        <>
            <Table striped hover>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col.dataField}>{col.text}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortCovers(covers).map((cover, index) => (
                        <tr key={index}>
                            {columns.map((col) => (
                                <td key={col.dataField}>
                                    {col.formatter ? col.formatter(getNestedProperty(cover, col.dataField), cover) : getNestedProperty(cover, col.dataField)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
}
